import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import {
  loadAllScripts,
  createSmartEditorInstance,
  saveEditorContent,
  destroyEditorInstance
} from '../utils/SmartEditorUtils';

// 스타일 정의
const editorStyles = {
  container: {
    width: '100%',
    height: '100%',
  },
  textarea: {
    width: '100%',
    minHeight: '400px',
    display: 'none' // 초기에는 숨김 (에디터가 이 위치에 렌더링됨)
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  errorContainer: {
    padding: '20px',
    color: 'red',
    border: '1px solid #f8d7da',
    backgroundColor: '#f8d7da',
    borderRadius: '4px',
    margin: '10px 0',
  }
};

/**
 * 네이버 스마트에디터2 컴포넌트
 * 
 * @param {Object} props - 컴포넌트 속성
 * @param {boolean} props.show - 모달 표시 여부
 * @param {Function} props.onCancel - 취소 버튼 클릭 시 콜백
 * @param {Function} props.onSave - 저장 버튼 클릭 시 콜백(에디터 내용을 인자로 전달)
 * @param {string} props.initialContent - 초기 에디터 내용
 * @param {string} props.title - 모달 제목
 * @param {boolean} props.loading - 콘텐츠 저장 중일 때 로딩 상태
 */
const SmartEditor2Component = ({
  show,
  onCancel,
  onSave,
  initialContent = '',
  title = '에디터',
  loading = false
}) => {
  // 고유 에디터 ID 생성
  const editorId = useRef(`smartEditor_${Date.now()}`).current;
  
  // 상태 관리
  const [isScriptLoading, setScriptLoading] = useState(true);
  const [isEditorLoading, setEditorLoading] = useState(true); 
  const [editorInstance, setEditorInstance] = useState(null);
  const [isInitialized, setInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // 에디터 스크립트 로드
  useEffect(() => {
    if (show) {
      let isMounted = true;
      
      const loadScripts = async () => {
        try {
          setScriptLoading(true);
          setError(null);
          
          // 스크립트 로드
          const loaded = await loadAllScripts();
          if (!loaded) {
            throw new Error('필수 스크립트 로드 실패');
          }
          
          if (isMounted) {
            setScriptLoading(false);
          }
        } catch (error) {
          console.error('스크립트 로드 오류:', error);
          if (isMounted) {
            setError(`스크립트 로드 오류: ${error.message}`);
            setScriptLoading(false);
          }
        }
      };
      
      loadScripts();
      
      return () => {
        isMounted = false;
      };
    }
  }, [show]);
  
  // 에디터 초기화
  useEffect(() => {
    if (show && !isScriptLoading && !isInitialized && !error) {
      let isMounted = true;
      
      const initEditor = async () => {
        try {
          setEditorLoading(true);
          
          // 에디터 인스턴스 생성
          const instance = await createSmartEditorInstance(editorId);
          
          if (isMounted) {
            setEditorInstance(instance);
            setInitialized(true);
            setEditorLoading(false);
          }
        } catch (error) {
          console.error('에디터 초기화 오류:', error);
          if (isMounted) {
            setError(`에디터 초기화 오류: ${error.message}`);
            setEditorLoading(false);
          }
        }
      };
      
      initEditor();
      
      return () => {
        isMounted = false;
      };
    }
  }, [show, isScriptLoading, isInitialized, error, editorId]);
  
  // 초기 콘텐츠 로드
  useEffect(() => {
    if (show && isInitialized && editorInstance && initialContent) {
      try {
        
        // textarea 값 설정
        const textarea = document.getElementById(editorId);
        if (textarea) {
          textarea.value = initialContent;
          
          // 에디터에 반영
          if (window.oEditors && window.oEditors.getById && window.oEditors.getById[editorId]) {
            window.oEditors.getById[editorId].exec('LOAD_CONTENTS_FIELD');
          }
        }
      } catch (error) {
        console.error('초기 콘텐츠 로드 오류:', error);
        setError(`초기 콘텐츠 로드 오류: ${error.message}`);
      }
    }
  }, [show, isInitialized, editorInstance, initialContent, editorId]);
  
  // 컴포넌트 언마운트 시 에디터 정리
  useEffect(() => {
    return () => {
      if (isInitialized && editorId) {
        try {
          destroyEditorInstance(editorId);
        } catch (error) {
          console.error('에디터 정리 오류:', error);
        }
      }
    };
  }, [isInitialized, editorId]);
  
  // 취소 버튼 처리
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };
  
  // 저장 버튼 처리
  const handleSave = async () => {
    try {
      if (!isInitialized || !editorInstance) {
        throw new Error('에디터가 초기화되지 않았습니다');
      }
      
      setIsSaving(true);
      
      // 에디터 내용 가져오기
      const content = await saveEditorContent(editorInstance, editorId);
      
      // 콜백 호출
      if (onSave) {
        onSave(content);
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('콘텐츠 저장 오류:', error);
      setError(`콘텐츠 저장 오류: ${error.message}`);
      setIsSaving(false);
    }
  };
  
  return (
    <Modal
      show={show}
      onHide={handleCancel}
      size="lg"
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && (
          <div style={editorStyles.errorContainer}>
            <p><strong>오류 발생:</strong> {error}</p>
            <p>다시 시도하거나 브라우저를 새로고침 해주세요.</p>
          </div>
        )}
        
        {(isScriptLoading || isEditorLoading) && !error ? (
          <div style={editorStyles.loadingContainer}>
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">
              {isScriptLoading ? '스크립트 로드 중...' : '에디터 초기화 중...'}
            </p>
          </div>
        ) : (
          <div style={editorStyles.container}>
            {/* 스마트에디터가 이 textarea를 대체하여 렌더링됨 */}
            <textarea
              id={editorId}
              name={editorId}
              style={editorStyles.textarea}
              defaultValue={initialContent}
            />
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={handleCancel}
          disabled={isSaving || loading}
        >
          취소
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={isScriptLoading || isEditorLoading || error || isSaving || loading}
        >
          {(isSaving || loading) && (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="mr-2"
            />
          )}
          {(isSaving || loading) ? '저장 중...' : '저장'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SmartEditor2Component; 